// The columns of an expense row on the overview screen, in both of its states.
//
// ONE BUILDER, NOT TWO COLUMN LISTS. The unpaid view and the paid archive show the SAME
// row with the same attribution — what it cost, what it was, which engagement it sits
// under, who the money goes to and whose money it was. The toggle changes which rows are
// on screen and nothing about how much each one says. Two hand-written lists would drift
// the first time a column was added to one of them, and the archive — the half nobody
// looks at until they need it — is the one that would quietly lose it.
//
// The one genuine difference is a consequence of the state: the vendor is "who we owe"
// while it is unpaid and "who we paid" once it is not.
//
// NO DATES. The owners keep none for paying suppliers; the server stamps both and the
// archive is still ordered by when a cost was marked paid.
import he from '../../i18n/he';
import PayerCell from '../../components/PayerCell';
import { payersLine } from '../../utils/payers';
import { expenseAmountColumns } from '../../components/expenseAmountColumns';
import ExpenseActions from '../../components/ExpenseActions';

// WHICH ENGAGEMENT A COST BELONGS TO, said the way the business says it: the job is what
// the work was, and the retainer above it is who it was for. A standalone job has no
// retainer, so the client answers instead — the cost still has to name somebody.
//
// Never rendered in the office section, where all three are NULL by construction.
const BelongsTo = ({ expense }) => (
  <div>
    <div className="strong">{expense.job_title || '—'}</div>
    <div className="muted small-note">
      {expense.retainer_title || expense.client_name || '—'}
    </div>
  </div>
);

export const expenseColumns = ({ paid, section, lookups, onMarkPaid, onEdit, onDelete }) => [
  { key: 'category', label: he.jobs.category, primary: true, render: (r) => lookups.categoryLabel(r.category) },
  { key: 'description', label: he.jobs.description, render: (r) => r.description || '—' },
  // What we asked the client for, what it cost us, who bears it — the job panel's own
  // three columns. On the office section the bearer is "לא צוין" on every row by
  // construction (an office cost has no client) and nothing was asked, so a dash.
  ...expenseAmountColumns,
  // Only where it can say anything. An office cost belongs to no job, which is the whole
  // definition of it, so the column would be a row of dashes.
  ...(section === 'jobs'
    ? [{ key: 'belongs_to', label: he.expenseOverview.belongsTo, render: (r) => <BelongsTo expense={r} /> }]
    : []),
  {
    key: 'vendor',
    label: paid ? he.expenseOverview.vendor : he.expenseOverview.vendorUnpaid,
    render: (r) => r.vendor || '—',
  },
  // Whose money left — or, while it has not, that nobody's has and who is out of pocket
  // waiting. The same cell the job panel uses, so the three states read identically
  // wherever an expense is listed.
  { key: 'payers', label: he.jobs.payer, render: (r) => <PayerCell expense={r} />, sortValue: (r) => payersLine(r.payers) },
  {
    key: 'action',
    label: he.common.actions,
    action: true,
    render: (r) => (
      // "Mark as paid" hides itself on a paid row — ExpenseActions asks `isUnpaid`, so the
      // archive offers edit and delete and nothing that would re-settle a settled cost.
      <ExpenseActions expense={r} onMarkPaid={onMarkPaid} onEdit={onEdit} onDelete={onDelete} />
    ),
  },
];
